import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { randomUuid } from '../lib/randomUuid'
import { useTheme } from '../theme/ThemeContext'

export type ToastVariant = 'default' | 'info' | 'success' | 'warning' | 'error'

/** 未指定 {@link ToastShowOptions.autoCloseMs} 时的默认停留时间 */
const DEFAULT_AUTO_CLOSE_MS = 3800

export type ToastShowOptions = {
  message: string
  variant?: ToastVariant
  /**
   * 自动关闭延迟（毫秒）。不传则用默认 {@link DEFAULT_AUTO_CLOSE_MS}；
   * 传 `0` 表示不自动关闭（需手动点关闭），仅少数场景使用。
   */
  autoCloseMs?: number
}

type ToastRecord = Omit<ToastShowOptions, 'variant'> & {
  id: string
  variant: ToastVariant
}

type ToastContextValue = {
  showToast: (options: ToastShowOptions) => string
  dismissToast: (id: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const TOAST_DEDUPE_MS = 2800

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastRecord[]>([])
  const recentToastRef = useRef<{ message: string; variant: ToastVariant; at: number } | null>(
    null,
  )

  const showToast = useCallback((options: ToastShowOptions) => {
    const message = options.message.trim()
    const variant = options.variant ?? 'default'
    const now = Date.now()
    const recent = recentToastRef.current
    if (
      recent &&
      recent.message === message &&
      recent.variant === variant &&
      now - recent.at < TOAST_DEDUPE_MS
    ) {
      return ''
    }
    recentToastRef.current = { message, variant, at: now }
    const id = randomUuid()
    const record: ToastRecord = {
      id,
      message,
      variant,
      autoCloseMs:
        options.autoCloseMs === undefined ? DEFAULT_AUTO_CLOSE_MS : options.autoCloseMs,
    }
    setToasts((prev) => [record, ...prev])
    return id
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const value = useMemo(
    () => ({ showToast, dismissToast }),
    [showToast, dismissToast],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <ToastStack toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  )
}

export function useToast() {
  const ctx = useContext(ToastContext)
  if (!ctx) throw new Error('useToast 须在 ToastProvider 内使用')
  return ctx
}

function ToastStack({
  toasts,
  onDismiss,
}: {
  toasts: ToastRecord[]
  onDismiss: (id: string) => void
}) {
  const { theme } = useTheme()
  const angular = theme === 'pixel' || theme === 'geek'

  if (toasts.length === 0) return null

  return (
    <div
      className="pointer-events-none fixed right-4 top-4 z-[520] flex max-w-[min(26rem,calc(100vw-2rem))] flex-col gap-3"
      aria-live="polite"
    >
      {toasts.map((t) => (
        <ToastBubble
          key={t.id}
          toast={t}
          angular={angular}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  )
}

function ToastBubble({
  toast,
  angular,
  onDismiss,
}: {
  toast: ToastRecord
  angular: boolean
  onDismiss: (id: string) => void
}) {
  useEffect(() => {
    const ms = toast.autoCloseMs
    if (ms == null || ms <= 0) return
    const timer = window.setTimeout(() => onDismiss(toast.id), ms)
    return () => window.clearTimeout(timer)
  }, [toast.autoCloseMs, toast.id, onDismiss])

  const role = toast.variant === 'error' ? 'alert' : 'status'

  return (
    <div
      role={role}
      className={[
        'glyphicai-toast-bubble pointer-events-auto relative w-full min-w-0 border border-line pl-4 pr-10 py-3 text-left',
        `glyphicai-toast-variant--${toast.variant}`,
        angular ? 'glyphicai-toast-bubble--angular rounded-none' : 'rounded-[var(--radius-sm)]',
      ].join(' ')}
    >
      <p className="min-w-0 break-words text-sm font-normal leading-snug text-ink [overflow-wrap:anywhere]">
        {toast.message}
      </p>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className={[
          'absolute right-1.5 top-1.5 inline-flex size-8 items-center justify-center text-ink-soft outline-none transition',
          'hover:bg-surface-muted hover:text-ink',
          'focus-visible:ring-2 focus-visible:ring-accent',
          angular ? 'rounded-none' : 'rounded-full',
        ].join(' ')}
        aria-label="关闭通知"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          className="size-4"
          aria-hidden
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
